import { UUID } from "crypto";
import { baseApi } from "./baseApi";
import { Response, ResponsePagination } from "@interfaces/response";
import { Deed, DictamenHistory, Empowered, Ruling } from "@interfaces/dictamen";

const dictamenApi = baseApi.injectEndpoints({
    endpoints: (build) => ({
        getDeeds: build.query<Array<Deed>, string>({
            query: (params) => ({
                url: `dictamen/escrituras?${params}`,
            }),
            transformResponse: (response: ResponsePagination<Deed>) => {
                return response.data.data;
            },
        }),
        postDeed: build.mutation<Deed, any>({
            query: (body) => ({
                url: "dictamen/escrituras",
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<Deed>) => {
                return response.data;
            },
        }),
        patchDeed: build.mutation<Deed, { deedId: UUID; body: any }>({
            query: ({ deedId, body }) => ({
                url: `dictamen/escrituras/${deedId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<Deed>) => {
                return response.data;
            },
        }),
        getDeed: build.query<Deed, UUID>({
            query: (deedId) => ({
                url: `dictamen/escrituras/${deedId}`,
            }),
            transformResponse: (response: Response<Deed>) => {
                return response.data;
            },
        }),
        getRulings: build.query<Array<Ruling>, string>({
            query: (params) => ({
                url: `dictamen?${params}`,
            }),
            transformResponse: (response: ResponsePagination<Ruling>) => {
                return response.data.data;
            },
        }),
        postRuling: build.mutation<Ruling, any>({
            query: (body) => ({
                url: "dictamen",
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<Ruling>) => {
                return response.data;
            },
        }),
        patchRuling: build.mutation<Ruling, { rulingId: UUID; body: any }>({
            query: ({ rulingId, body }) => ({
                url: `dictamen/${rulingId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<Ruling>) => {
                return response.data;
            },
        }),
        getEmpowered: build.query<Array<Empowered>, string>({
            query: (params) => ({
                url: `dictamen/facultados?${params}`,
            }),
            transformResponse: (response: ResponsePagination<Empowered>) => {
                return response.data.data;
            },
        }),
        postEmpowered: build.mutation<Empowered, Partial<Empowered>>({
            query: (body) => ({
                url: `dictamen/facultados`,
                method: "POST",
                body: body,
            }),
            transformResponse: (response: Response<Empowered>) => {
                return response.data;
            },
        }),
        patchEmpowered: build.mutation<Empowered, { empoweredId: UUID; body: any }>({
            query: ({ empoweredId, body }) => ({
                url: `dictamen/facultados/${empoweredId}`,
                method: "PATCH",
                body: body,
            }),
            transformResponse: (response: Response<Empowered>) => {
                return response.data;
            },
        }),
        deleteEmpowered: build.mutation<void, UUID>({
            query: (empoweredId) => ({
                url: `dictamen/facultados/${empoweredId}`,
                method: "DELETE",
            }),
        }),
        getHistoryChanges: build.query<DictamenHistory, UUID>({
            query: (loanAppId) => ({
                url: `dictamen/history/${loanAppId}`,
            }),
            transformResponse: (response: Response<DictamenHistory>) => {
                return response.data;
            },
        }),
    }),
});

export const {
    useLazyGetDeedsQuery,
    usePostDeedMutation,
    usePatchDeedMutation,
    useLazyGetDeedQuery,
    useLazyGetRulingsQuery,
    usePostRulingMutation,
    usePatchRulingMutation,
    useLazyGetEmpoweredQuery,
    usePostEmpoweredMutation,
    usePatchEmpoweredMutation,
    useDeleteEmpoweredMutation,
    useLazyGetHistoryChangesQuery,
} = dictamenApi;
